import { parseDateOnly, toInputDate } from './dateOnly';
import {
  capMsToDailyLimit,
  endOfIstDayMs,
  formatIstTime,
  MAX_IST_DAY_MS,
  parseApiDateTimeMs,
  toIstDateString,
} from './istTime';

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

function startOfNextIstDayMs(ms) {
  const dateStr = toIstDateString(ms);
  if (!dateStr) return Number.NaN;
  const [y, m, d] = dateStr.split('-').map(Number);
  return Date.UTC(y, m - 1, d + 1, 0, 0, 0, 0) - IST_OFFSET_MS;
}
function hasLogoutTime(value) {
  if (value == null || value === '') return false;
  return !Number.isNaN(parseApiDateTimeMs(value));
}

function sessionEndTime(login, logout, referenceNow = Date.now()) {
  const loginMs = parseApiDateTimeMs(login);
  if (Number.isNaN(loginMs)) return loginMs;

  if (hasLogoutTime(logout)) {
    const logoutMs = parseApiDateTimeMs(logout);
    return Number.isNaN(logoutMs) ? loginMs : logoutMs;
  }

  const refMs = referenceNow instanceof Date ? referenceNow.getTime() : referenceNow;
  const loginDateStr = toIstDateString(loginMs);
  const refDateStr = toIstDateString(refMs);
  if (loginDateStr === refDateStr) return refMs;

  return endOfIstDayMs(loginMs);
}

function mergeIntervalMs(intervals) {
  if (!intervals?.length) return 0;

  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  let total = 0;
  let [curStart, curEnd] = sorted[0];

  for (let i = 1; i < sorted.length; i += 1) {
    const [nextStart, nextEnd] = sorted[i];
    if (nextStart <= curEnd) {
      curEnd = Math.max(curEnd, nextEnd);
    } else {
      total += curEnd - curStart;
      curStart = nextStart;
      curEnd = nextEnd;
    }
  }

  total += curEnd - curStart;
  return Math.min(total, MAX_IST_DAY_MS);
}

export function buildLoginTimeMap(logs, referenceNow = Date.now()) {
  const intervalsByKey = new Map();
  const refMs = referenceNow instanceof Date ? referenceNow.getTime() : referenceNow;

  (logs || []).forEach((log) => {
    const userId = log.userId ?? log.UserId;
    const login = log.userLoginDate ?? log.UserLoginDate;
    const logout = log.userLogoutDate ?? log.UserLogoutDate;
    if (!userId || !login) return;

    const loginMs = parseApiDateTimeMs(login);
    const endMs = sessionEndTime(login, logout, refMs);
    if (Number.isNaN(loginMs) || Number.isNaN(endMs) || endMs <= loginMs) {
      return;
    }

    let cursor = loginMs;
    let guard = 0;
    while (cursor < endMs && guard < 366) {
      guard += 1;
      const dayStr = toIstDateString(cursor);
      if (!dayStr) break;

      const dayEnd = endOfIstDayMs(cursor);
      const segmentEnd = endMs <= dayEnd ? endMs : dayEnd;
      if (segmentEnd > cursor) {
        const key = `${userId}|${dayStr}`;
        if (!intervalsByKey.has(key)) intervalsByKey.set(key, []);
        intervalsByKey.get(key).push([cursor, segmentEnd]);
      }

      const next = startOfNextIstDayMs(cursor);
      if (Number.isNaN(next) || next <= cursor) break;
      cursor = next;
    }
  });

  const map = new Map();
  intervalsByKey.forEach((intervals, key) => {
    map.set(key, mergeIntervalMs(intervals));
  });

  return map;
}

/** Cap per-day task/meeting maps so they never exceed login time or 24h. */
export function capTimeMapToLogin(timeMap, loginMap) {
  const capped = new Map();
  (timeMap || new Map()).forEach((ms, key) => {
    const loginMs = loginMap?.get(key) ?? MAX_IST_DAY_MS;
    capped.set(key, Math.min(capMsToDailyLimit(ms), capMsToDailyLimit(loginMs)));
  });
  return capped;
}

export function formatDurationMs(ms) {
  const totalSeconds = Math.max(0, Math.floor(Number(ms) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function hmsToMs(value) {
  if (!value || typeof value !== 'string') return 0;
  const clean = value.trim().replace(/^-/, '');
  const parts = clean.split(':').map((part) => Number(part));
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) return 0;
  const [hours, minutes, seconds] = parts;
  return Math.max(0, ((hours * 3600) + (minutes * 60) + seconds) * 1000);
}

export function computeNeutralMs(totalMs, productiveHms) {
  const productiveMs = hmsToMs(productiveHms);
  const cappedProductiveMs = Math.min(productiveMs, Math.max(0, totalMs));
  return Math.max(0, totalMs - cappedProductiveMs);
}

export function formatProductiveForTotal(totalMs, productiveHms) {
  const productiveMs = hmsToMs(productiveHms);
  return formatDurationMs(Math.min(productiveMs, Math.max(0, totalMs)));
}

export function getReportFetchRange(mode, startDate, endDate) {
  const fallback = toInputDate(new Date());
  const to = toInputDate(endDate) || toInputDate(startDate) || fallback;
  const from = toInputDate(startDate) || to;

  if (mode === 'daily') {
    return { fromDate: to, toDate: to };
  }

  return { fromDate: from, toDate: to };
}

export function getDefaultReportDateRange(daysBack = 6) {
  const today = new Date();
  const endDate = toInputDate(today);
  const start = new Date(today);
  start.setDate(start.getDate() - daysBack);
  return { startDate: toInputDate(start), endDate };
}

function startOfIstDayMs(dateStr) {
  if (!dateStr) return Number.NaN;
  const [y, m, d] = dateStr.split('-').map(Number);
  return Date.UTC(y, m - 1, d, 0, 0, 0, 0) - IST_OFFSET_MS;
}

/**
 * Earliest work start per user per IST day — aligned with Total Time segments.
 * - Same-day login (including night): actual login time
 * - Session carried from previous day: midnight IST on that day
 */
export function buildFirstLoginStartMap(logs, referenceNow = Date.now()) {
  const map = new Map();
  const refMs = referenceNow instanceof Date ? referenceNow.getTime() : referenceNow;

  (logs || []).forEach((log) => {
    const userId = log.userId ?? log.UserId;
    const login = log.userLoginDate ?? log.UserLoginDate;
    const logout = log.userLogoutDate ?? log.UserLogoutDate;
    if (!userId || !login) return;

    const loginMs = parseApiDateTimeMs(login);
    if (Number.isNaN(loginMs)) return;

    const endMs = sessionEndTime(login, logout, refMs);
    if (Number.isNaN(endMs) || endMs <= loginMs) return;

    const loginDayStr = toIstDateString(loginMs);
    let cursor = loginMs;
    let guard = 0;

    while (cursor < endMs && guard < 366) {
      guard += 1;
      const dayStr = toIstDateString(cursor);
      if (!dayStr) break;

      const dayStartMs = startOfIstDayMs(dayStr);
      const dayEnd = endOfIstDayMs(cursor);
      const segmentEnd = endMs <= dayEnd ? endMs : dayEnd;
      if (segmentEnd <= dayStartMs) break;

      const effectiveStartMs = dayStr === loginDayStr ? loginMs : dayStartMs;
      const key = `${userId}|${dayStr}`;
      const existing = map.get(key);
      if (existing == null || effectiveStartMs < existing) {
        map.set(key, effectiveStartMs);
      }

      const next = startOfNextIstDayMs(cursor);
      if (Number.isNaN(next) || next <= cursor) break;
      cursor = next;
    }
  });

  return map;
}

const IST_SEVEN_AM_MS = 7 * 60 * 60 * 1000;

/** Earliest actual login on workDate (IST) at or after 7:00 AM — for dashboard Start Time display. */
export function getEarliestLoginOnDateAfter7Am(logs, userId, workDate) {
  if (!userId || !workDate) return null;

  const sevenAmMs = startOfIstDayMs(workDate) + IST_SEVEN_AM_MS;
  if (Number.isNaN(sevenAmMs)) return null;

  let earliestMs = null;

  (logs || []).forEach((log) => {
    const logUserId = log.userId ?? log.UserId;
    if (Number(logUserId) !== Number(userId)) return;

    const login = log.userLoginDate ?? log.UserLoginDate;
    if (!login) return;

    const loginMs = parseApiDateTimeMs(login);
    if (Number.isNaN(loginMs)) return;
    if (toIstDateString(loginMs) !== workDate) return;
    if (loginMs < sevenAmMs) return;

    if (earliestMs == null || loginMs < earliestMs) {
      earliestMs = loginMs;
    }
  });

  return earliestMs;
}

export function isOnOrAfter7AmIst(value, workDate) {
  const ms = typeof value === 'number' ? value : parseApiDateTimeMs(value);
  if (Number.isNaN(ms) || !workDate) return false;
  if (toIstDateString(ms) !== workDate) return false;
  const sevenAmMs = startOfIstDayMs(workDate) + IST_SEVEN_AM_MS;
  return !Number.isNaN(sevenAmMs) && ms >= sevenAmMs;
}

export function formatLoginStartTime(value) {
  return formatIstTime(value);
}
